import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { formatFieldLabel, normalizeValue, parseMaybeJson } from '../../../helpers/PartnerHelper';
import ConflictIssuesTable from './ConflictIssuesTable';

export const getConflictOptions = (conflict) => {
  const type = String(conflict?.conflict_type || '');
  if (type === 'PARTNER_NIF_STAT_DIFF') {
    return [
      { value: 'KEEP_PGI_IDS', label: 'Conserver NIF / STAT enregistrés' },
      { value: 'APPLY_IMPORTED_IDS', label: 'Appliquer NIF / STAT du fichier' },
      { value: 'IGNORE_ROW', label: 'Ignorer la ligne' },
    ];
  }

  if (type === 'PARTNER_EMAIL_DUPLICATE') {
    return [
      { value: 'KEEP_PGI_EMAIL', label: 'Conserver l’adresse e-mail enregistrée' },
      { value: 'IGNORE_ROW', label: 'Ignorer la ligne' },
    ];
  }

  if (type === 'STAFF_CIN_AMBIGUOUS') {
    const data = parseMaybeJson(conflict?.conflict_data, {});
    const candidates = Array.isArray(data?.existing?.candidates) ? data.existing.candidates : [];
    return [
      ...candidates.map((candidate) => ({
        value: `USE_EXISTING_STAFF:${candidate.personnel_id}`,
        label: `Utiliser ${candidate.nom_complet || 'Personnel'} — ID ${candidate.personnel_id}`,
      })),
      { value: 'IGNORE_ROW', label: 'Ignorer la ligne' },
    ];
  }

  return [
    { value: 'IGNORE_ROW', label: 'Ignorer la ligne' },
  ];
};

export const toResolutionPayload = (choice) => {
  const staffMatch = /^USE_EXISTING_STAFF:(\d+)$/.exec(choice);
  return {
    resolution: staffMatch ? 'USE_EXISTING_STAFF' : choice,
    resolution_data: staffMatch ? { personnel_id: Number(staffMatch[1]) } : {},
  };
};

const ENTITY_LABELS = {
  PARTNER: 'Partenaire',
  MARKET: 'Marché',
  STAFF: 'Personnel',
};

const CONFLICT_LABELS = {
  PARTNER_NIF_STAT_DIFF: 'NIF ou STAT différent des données PGI',
  PARTNER_IDENTITY_AMBIGUOUS: 'NIF et STAT correspondent à des partenaires différents',
  PARTNER_EMAIL_DUPLICATE: 'Email déjà utilisé par un autre partenaire',
  STAFF_CIN_AMBIGUOUS: 'CIN associé à plusieurs personnels',
  PARTNER_NIF_STAT_NAME_DIFF: 'NIF et STAT identiques, mais nom différent',
  PARTNER_NAME_NIF_DIFF: 'Nom identique, mais NIF différent',
  PARTNER_NAME_STAT_DIFF: 'Nom identique, mais STAT différent',
  PARTNER_NAME_EXISTS: 'Partenaire déjà existant',
  MARKET_NUMBER_EXISTS: 'Numéro de marché déjà existant',
  MARKET_NUMBER_DIFF: 'Numéro de marché différent',
  STAFF_NAME_CIN_DIFF: 'Nom du personnel identique, mais CIN différent',
  STAFF_CIN_EXISTS: 'CIN déjà existant',
  STAFF_NAME_EXISTS: 'Personnel déjà existant',
};

const FIELD_LABELS = {
  partenaire: 'Partenaire',
  raison_sociale: 'Partenaire',
  NomPartenaire: 'Partenaire',
  nif: 'NIF',
  NIF: 'NIF',
  stat: 'STAT',
  STAT: 'STAT',
  numero_marche: 'Numéro marché',
  numero_marche_source: 'Numero marche', // compatibilite staging anterieur
  ref_num_contrat: 'Numéro marché',
  NumeroMarche: 'Numéro marché',
  objet_contrat: 'Objet du contrat',
  ObjetContrat: 'Objet du contrat',
  nom_personnel: 'Nom personnel',
  nom_complet: 'Nom personnel',
  NomPersonnel: 'Nom personnel',
  cin: 'CIN',
  CIN: 'CIN',
  fonction: 'Fonction',
  Fonction: 'Fonction',
  consultantIndividuel: 'Consultant individuel',
  consultant_individuel: 'Consultant individuel',
  isConsultantIndividuel: 'Consultant individuel',
  is_consultant_individuel: 'Consultant individuel',
};

const getEntityLabel = (entityType) => ENTITY_LABELS[entityType] || entityType || 'Conflit';

const getConflictTypeLabel = (conflictType) => (
  CONFLICT_LABELS[conflictType] || formatFieldLabel(conflictType || 'Conflit')
);

const getFieldLabel = (key) => FIELD_LABELS[key] || formatFieldLabel(key);

const isBeneficiaryField = (key) => {
  const normalized = String(key || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

  return normalized.includes('beneficiaire')
    || normalized.includes('beneficiary')
    || normalized.includes('menage')
    || normalized.includes('menages');
};

const isConsultantIndividuelField = (key) => {
  const normalized = String(key || '')
    .replace(/_/g, '')
    .toLowerCase();

  return normalized.includes('consultantindividuel');
};

const formatDisplayValue = (key, value) => {
  if (isConsultantIndividuelField(key)) {
    const normalized = String(value ?? '').trim().toLowerCase();
    return value === true || normalized === 'true' || normalized === '1' || normalized === 'oui'
      ? 'Oui'
      : 'Non';
  }

  return normalizeValue(value);
};

const getConflictPayload = (conflict) => {
  const data = parseMaybeJson(conflict?.conflict_data, {});
  return {
    imported: data?.imported && typeof data.imported === 'object' ? data.imported : {},
    existing: data?.existing && typeof data.existing === 'object' ? data.existing : {},
  };
};

const getConflictLabel = (conflict) => {
  const { imported, existing } = getConflictPayload(conflict);
  const importedName = imported.NomPartenaire
    || imported.NomPersonnel
    || imported.NumeroMarche
    || imported.partenaire
    || imported.nom_personnel
    || imported.ref_num_contrat
    || imported.numero_marche_source // compatibilite staging anterieur
    || imported.raison_sociale
    || '';
  const existingName = existing.raison_sociale
    || existing.nom_complet
    || existing.nom
    || existing.numero_marche
    || existing.NumeroMarche
    || '';
  return [getConflictTypeLabel(conflict?.conflict_type), importedName && `Excel : ${importedName}`, existingName && `Base : ${existingName}`]
    .filter(Boolean)
    .join(' - ');
};

const toDisplayEntries = (data) => (
  Object.entries(data || {})
    .filter(([key, value]) => !key.startsWith('_') && !isBeneficiaryField(key) && normalizeValue(value) !== '')
    .slice(0, 18)
);

const getUpdatedFields = (imported, existing) => {
  const existingKeys = new Set(Object.keys(existing || {}));
  const changedFields = Object.entries(imported || {})
    .filter(([key, value]) => (
      normalizeValue(value) !== ''
      && existingKeys.has(key)
      && normalizeValue(value) !== normalizeValue(existing?.[key])
    ))
    .map(([key, value]) => ({
      key,
      from: existing?.[key],
      to: value,
    }));

  if (changedFields.length > 0) {
    return changedFields;
  }

  return Object.entries(imported || {})
    .filter(([, value]) => normalizeValue(value) !== '')
    .slice(0, 6)
    .map(([key, value]) => ({
      key,
      from: existing?.[key],
      to: value,
    }));
};

const decisionMeta = {
  KEEP_PGI_IDS: {
    title: 'Décision : conserver les identifiants PGI',
    help: 'Le nom PGI reste inchangé. NIF et STAT existants sont conservés.',
    tone: 'warning',
  },
  APPLY_IMPORTED_IDS: {
    title: 'Décision : appliquer NIF et STAT du fichier',
    help: 'Le nom PGI reste inchangé. Seuls NIF et STAT sont remplacés.',
    tone: 'info',
  },
  KEEP_PGI_EMAIL: {
    title: 'Décision : conserver l’email PGI',
    help: 'Les autres données de la ligne seront mises à jour sans réutiliser l’email en doublon.',
    tone: 'warning',
  },
  USE_EXISTING_STAFF: {
    title: 'Décision : utiliser le personnel sélectionné',
    help: 'La ligne sera rattachée à cette personne existante et ses informations pourront être mises à jour.',
    tone: 'info',
  },
  IGNORE_ROW: {
    title: 'Décision : ignorer la ligne',
    help: 'Cette ligne ne sera pas synchronisée et restera traçable.',
    tone: 'warning',
  },
};

const DataLine = ({ title, subtitle, data, crossed, badge }) => {
  const entries = toDisplayEntries(data);

  return (
    <Box
      sx={{
        position: 'relative',
        border: '1px solid #dbe4ef',
        borderRadius: 2,
        p: 1.5,
        backgroundColor: crossed ? '#f8fafc' : '#ffffff',
        opacity: crossed ? 0.58 : 1,
        textDecoration: crossed ? 'line-through' : 'none',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <Box>
          <Typography sx={{ fontWeight: 800, color: '#1f2a44' }}>{title}</Typography>
          <Typography sx={{ fontSize: 12, color: '#64748b' }}>{subtitle}</Typography>
        </Box>
        {badge ? (
          <Chip size="small" color={badge.color} label={badge.label} sx={{ fontWeight: 700 }} />
        ) : null}
      </Box>

      {entries.length > 0 ? (
        <TableContainer
          sx={{
            border: '1px solid #edf2f7',
            borderRadius: 1.5,
            overflowX: 'auto',
            overflowY: 'hidden',
            maxWidth: '100%',
          }}
        >
          <Table size="small" sx={{ minWidth: entries.length * 150 }}>
            <TableHead>
              <TableRow>
                {entries.map(([key]) => (
                  <TableCell
                    key={`head-${key}`}
                    sx={{
                      minWidth: 150,
                      backgroundColor: '#f8fbff',
                      borderBottom: '1px solid #dbe4ef',
                      fontSize: 12,
                      fontWeight: 800,
                      color: '#64748b',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {getFieldLabel(key)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                {entries.map(([key, value]) => (
                  <TableCell
                    key={`value-${key}`}
                    sx={{
                      borderBottom: '1px solid #edf2f7',
                      minWidth: 150,
                      fontSize: 13,
                      color: '#24324b',
                      overflowWrap: 'anywhere',
                      verticalAlign: 'top',
                    }}
                  >
                    {formatDisplayValue(key, value)}
                  </TableCell>
                ))}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography sx={{ fontSize: 13, color: '#94a3b8' }}>Aucune donnee disponible.</Typography>
      )}
    </Box>
  );
};

const UpdatedFields = ({ fields }) => {
  if (!fields.length) {
    return null;
  }

  return (
    <Box sx={{ mt: 1.25, p: 1.25, borderRadius: 1.5, backgroundColor: '#eef6ff', border: '1px solid #bfdbfe' }}>
      <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#1d4ed8', mb: 0.75 }}>
        Champs qui seront mis a jour
      </Typography>
      <Stack spacing={0.75}>
        {fields.map((field) => (
          <Box key={field.key} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '160px 1fr' }, gap: 0.75 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#334155' }}>
              {getFieldLabel(field.key)}
            </Typography>
            <Typography sx={{ fontSize: 12, color: '#475569' }}>
              <Box component="span" sx={{ color: '#94a3b8' }}>{normalizeValue(field.from) || 'vide'}</Box>
              {' -> '}
              <Box component="span" sx={{ color: '#1d4ed8', fontWeight: 700 }}>{normalizeValue(field.to)}</Box>
            </Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

const ConflitManage = (props) => {
  const {
    open = false,
    onClose,
    importConflicts = [],
    importAnomalies = [],
    anomalyRules = {},
    conflictDecisions = {},
    setConflictDecisions,
    handleResolveImportConflicts,
    onCancelImport,
    onReplaceFile,
    resolvingConflicts = false,
    isSubmitting = false,
  } = props;

  if (!open || importConflicts.length + importAnomalies.length === 0) {
    return null;
  }

  const groups = Object.values([...importAnomalies, ...importConflicts].reduce((result, issue) => {
    const raw = parseMaybeJson(issue.raw_data, {});
    const key = raw._source_sheet && raw._source_row
      ? `${raw._source_sheet}:${raw._source_row}` : `issue:${issue.id}`;
    if (!result[key]) result[key] = { key, raw, issues: [] };
    result[key].issues.push(issue);
    return result;
  }, {}));

  const ignoreGroup = (group) => {
    if (typeof setConflictDecisions !== 'function') return;
    setConflictDecisions((current) => {
      const next = { ...current };
      group.issues.forEach((issue) => { next[issue.id] = 'IGNORE_ROW'; });
      return next;
    });
  };

  return (
    <Dialog open={open} scroll="paper" onClose={(_, reason) => { if (reason !== 'backdropClick') onClose?.(); }} maxWidth={false} fullWidth PaperProps={{ sx: { width: 'calc(100vw - 16px)', maxWidth: 2200, maxHeight: 'calc(100vh - 32px)', borderRadius: 2, border: '1px solid #cbd8e6' } }}>
      <DialogTitle sx={{ backgroundColor: '#173d61', color: '#ffffff', fontSize: 18, fontWeight: 800 }}>
        Gestion des lignes à examiner
      </DialogTitle>
      <DialogContent dividers sx={{ backgroundColor: '#f6f9fc', maxHeight: 'calc(100vh - 176px)', overflowY: 'auto' }}>
        <Alert
          icon={<WarningAmberRoundedIcon fontSize="inherit" />}
          severity="warning"
          sx={{
            mb: 2,
            borderRadius: 2,
            border: '1px solid #f5ddb5',
            backgroundColor: '#fff8eb',
            color: '#3f3a2f',
            '& .MuiAlert-message': { width: '100%' },
          }}
        >
          <Typography sx={{ fontWeight: 800, mb: 0.5 }}>
            Corrigez le fichier ou décidez du traitement de chaque ligne.
          </Typography>
          <Typography sx={{ fontSize: 14 }}>
            Une ligne ignorée ne sera pas synchronisée, même si elle présente plusieurs problèmes.
          </Typography>
        </Alert>

        <ConflictIssuesTable
          importConflicts={importConflicts}
          importAnomalies={importAnomalies}
          conflictDecisions={conflictDecisions}
          setConflictDecisions={setConflictDecisions}
          getConflictOptions={getConflictOptions}
          resolvingConflicts={resolvingConflicts}
          isSubmitting={isSubmitting}
        />
        <Stack spacing={2} sx={{ display: 'none' }}>
          {groups.map((group) => {
            const ignored = group.issues.some((issue) => conflictDecisions[issue.id] === 'IGNORE_ROW');
            return (
              <Box
                key={group.key}
                sx={{
                  border: '1px solid #dbe4ef',
                  borderRadius: 2,
                  overflow: 'hidden',
                  backgroundColor: '#ffffff',
                }}
              >
                <Box sx={{ p: 1.5, backgroundColor: '#f8fbff', borderBottom: '1px solid #dbe4ef' }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                    <Box>
                      <Typography sx={{ fontWeight: 800, color: '#1f2a44' }}>
                        {group.raw._source_sheet || 'canevas'} — ligne {group.raw._source_row || '-'}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: '#64748b' }}>
                        {group.raw.partenaire || group.raw.nom_personnel || group.raw.ref_num_contrat || 'Données du fichier'}
                      </Typography>
                    </Box>
                    <Button size="small" variant="outlined" color="warning" onClick={() => ignoreGroup(group)} disabled={resolvingConflicts || isSubmitting}>
                      {ignored ? 'Ligne ignorée' : 'Ignorer la ligne'}
                    </Button>
                  </Stack>
                </Box>

                <Box sx={{ p: 1.5 }}>
                  <Stack spacing={1.25}>
                    <DataLine
                      title="Ligne du fichier Excel"
                      subtitle="Données reçues"
                      data={group.raw}
                      crossed={ignored}
                      badge={ignored ? { label: 'Ignorée', color: 'warning' } : null}
                    />
                    {group.issues.map((issue) => {
                      const { imported, existing } = getConflictPayload(issue);
                      const anomaly = issue.status === 'BLOCKING_ERROR';
                      const selected = conflictDecisions[issue.id] || (anomaly ? '' : getConflictOptions(issue)[0]?.value || '');
                      const decisionType = selected.startsWith('USE_EXISTING_STAFF:') ? 'USE_EXISTING_STAFF' : selected;
                      const meta = decisionMeta[decisionType];
                      return (
                        <Box key={issue.id} sx={{ p: 1.25, border: '1px solid #dbe4ef', borderRadius: 1.5 }}>
                          <Typography sx={{ fontWeight: 700, color: anomaly ? '#991b1b' : '#173d61' }}>
                            {getEntityLabel(issue.entity_type)} — {getConflictLabel(issue)}
                          </Typography>
                          {anomaly ? (
                            <Box sx={{ mt: 0.5 }}>
                              <Typography sx={{ fontSize: 13 }}>
                                {anomalyRules[issue.conflict_type] || 'Corrigez le fichier ou ignorez cette ligne.'}
                              </Typography>
                              {Array.isArray(imported.missing_fields) && imported.missing_fields.length > 0 && (
                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#991b1b' }}>
                                  Champs manquants : {imported.missing_fields.map(getFieldLabel).join(', ')}
                                </Typography>
                              )}
                            </Box>
                          ) : (
                            <Stack spacing={1} sx={{ mt: 1 }}>
                              <FormControl size="small" fullWidth>
                                <InputLabel id={`import-conflict-${issue.id}`}>Résolution</InputLabel>
                                <Select labelId={`import-conflict-${issue.id}`} label="Résolution" value={selected}
                                  onChange={(event) => setConflictDecisions((current) => ({ ...current, [issue.id]: event.target.value }))}>
                                  {getConflictOptions(issue).map((option) => (
                                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                              {meta && <Typography sx={{ fontSize: 12, color: '#64748b' }}>{meta.help}</Typography>}
                              <UpdatedFields fields={selected === 'APPLY_IMPORTED_IDS' ? getUpdatedFields(imported, existing) : []} />
                              {Object.keys(existing).length > 0 && (
                                <DataLine title="Données en base" subtitle="Référence actuelle PGI / SPM" data={existing} crossed={selected === 'APPLY_IMPORTED_IDS'} />
                              )}
                            </Stack>
                          )}
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>
              </Box>
            );
          })}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onCancelImport || onClose} color="secondary" variant="outlined" disabled={resolvingConflicts || isSubmitting}>
          Annuler l'import
        </Button>
        <Button onClick={onReplaceFile} variant="outlined" disabled={resolvingConflicts || isSubmitting}>
          Remplacer le fichier
        </Button>
        <Button
          onClick={handleResolveImportConflicts}
          variant="contained"
          disabled={resolvingConflicts || isSubmitting || typeof handleResolveImportConflicts !== 'function'}
          sx={{ backgroundColor: '#173d61', '&:hover': { backgroundColor: '#102f4c' } }}
        >
          {resolvingConflicts ? (
            <CircularProgress size={20} sx={{ color: '#ffffff' }} />
          ) : (
            'Enregistrer les resolutions et soumettre'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConflitManage;
